import { apiFetch } from './apiBase';

function matchPerson(personNumber, personFilter) {
  if (!personFilter || !String(personFilter).trim()) return true;
  return String(personNumber || '').includes(String(personFilter).trim());
}

function paginate(list, { q, person, limit, offset }) {
  let rows = list || [];
  if (person) {
    rows = rows.filter((row) => matchPerson(row.personNumber, person));
  }
  if (q) {
    const needle = String(q).trim().toLowerCase();
    rows = rows.filter((row) => {
      const blob = Object.values(row || {})
        .map((v) => String(v ?? ''))
        .join(' ')
        .toLowerCase();
      return blob.includes(needle) || String(row.personNumber || '').includes(needle);
    });
  }
  const total = rows.length;
  const start = Math.max(0, Number(offset) || 0);
  const lim = Math.max(1, Number(limit) || 100);
  return { rows: rows.slice(start, start + lim), total, offset: start, limit: lim };
}

function personRows(ids) {
  return (ids || []).map((personNumber) => ({ personNumber: String(personNumber) }));
}

/** Load drill-down when /api/recon/metric-detail is missing (stale API). */
async function loadMetricDetailFallback(metric, filters = {}) {
  const { limit, offset, person, q, ...rest } = filters;
  if (metric === 'exceptions') {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    for (const [key, value] of Object.entries(rest)) {
      if (value != null && String(value).trim() !== '') params.set(key, String(value).trim());
    }
    const data = await apiFetch(`/api/recon/element-exceptions?${params}`);
    return {
      metric,
      label: 'Exceptions',
      kind: 'element',
      rows: data.exceptions || [],
      total: data.total || 0,
      offset,
      limit,
    };
  }

  const { summary } = await apiFetch('/api/recon/summary');
  const presence = summary?.personPresence || {};

  if (metric === 'matched') {
    try {
      const latest = await apiFetch('/api/recon/runs/latest');
      const all = latest.exceptions ? [] : [];
      void all;
    } catch (_) {
      /* ignore */
    }
    return {
      metric,
      label: 'Matched',
      kind: 'element',
      rows: [],
      total: summary?.matchedCount ?? summary?.comparisonStats?.match ?? 0,
      needsRerun: true,
      offset,
      limit,
    };
  }

  if (metric === 'inBoth') {
    const ids = presence.bothPersons?.length ? presence.bothPersons : [];
    const page = paginate(personRows(ids), { q, person, limit, offset });
    return {
      metric,
      label: 'In both systems',
      kind: 'person',
      needsRerun: !ids.length && (presence.inBoth || 0) > 0,
      ...page,
    };
  }
  if (metric === 'onlyOracle') {
    const ids = Array.isArray(presence.onlyOracle) && presence.onlyOracle.length ? presence.onlyOracle : [];
    return {
      metric,
      label: 'Only in Oracle',
      kind: 'person',
      needsRerun: !ids.length && (presence.onlyOracleCount || 0) > 0,
      ...paginate(personRows(ids), { q, person, limit, offset }),
    };
  }
  if (metric === 'onlyAdp') {
    const ids = Array.isArray(presence.onlyAdp) && presence.onlyAdp.length ? presence.onlyAdp : [];
    return {
      metric,
      label: 'Only in ADP',
      kind: 'person',
      needsRerun: !ids.length && (presence.onlyAdpCount || 0) > 0,
      ...paginate(personRows(ids), { q, person, limit, offset }),
    };
  }
  if (metric === 'oraclePersons') {
    const ids = presence.oraclePersonList?.length ? presence.oraclePersonList : [];
    return {
      metric,
      label: 'Oracle total persons',
      kind: 'person',
      needsRerun: !ids.length && (presence.oraclePersons || 0) > 0,
      ...paginate(personRows(ids), { q, person, limit, offset }),
    };
  }

  return null;
}

export async function fetchMetricDetail(metric, { limit, offset, ...filterFields }) {
  const params = new URLSearchParams({
    metric,
    limit: String(limit),
    offset: String(offset),
  });
  for (const [key, value] of Object.entries(filterFields)) {
    if (value != null && String(value).trim() !== '') params.set(key, String(value).trim());
  }
  try {
    return await apiFetch(`/api/recon/metric-detail?${params}`);
  } catch (e) {
    const routeMissing =
      e.status === 404 && /route not found|API not found/i.test(String(e.message || ''));
    if (!routeMissing) throw e;
    const fallback = await loadMetricDetailFallback(metric, {
      limit,
      offset,
      ...filterFields,
    });
    if (!fallback) {
      throw new Error(
        'Could not load this detail list. Refresh the page or try again.'
      );
    }
    return fallback;
  }
}
